import { useEffect, useState } from "react";
import { Link, useNavigate, useParams as useReactRouterParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { fpPath } from "@/lib/site";
import LangInput from "@/components/LangInput";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";
import ListenButton from "@/components/ListenButton";
import SoilWaterPanel from "@/components/SoilWaterPanel";
import {
  House, Plus, MapPin, ArrowRight, ArrowLeft, Trash, PencilSimple,
  Notebook, Leaf, Drop, Sparkle, EyeSlash, Eye, ShareNetwork, PaperPlaneTilt,
} from "@phosphor-icons/react";

/**
 * FarmersFarms — /farmers/farms
 * Lists the signed-in user's farms with quick access to the diary and edit
 * flows. Unauthenticated visitors are prompted to sign in.
 */
export function FarmersFarms() {
  const { user, loading, requireSignIn } = useAuth();
  const [farms, setFarms] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { setBusy(false); return; }
    http.get("/farmers/farms/mine")
      .then(({ data }) => setFarms(data.farms || []))
      .catch(() => toast.error("Could not load your farms"))
      .finally(() => setBusy(false));
  }, [user, loading]);

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="farmers-farms-page">
      <div className="max-w-[1100px] mx-auto">
        <Link to={fpPath("/")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <h1 className="font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">Your farms</h1>
          <Link
            to={fpPath("/farms/new")}
            data-testid="farmers-farms-add"
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Plus size={11} weight="bold" /> New farm
          </Link>
        </div>

        {!user ? (
          <div className="mt-8 rounded-2xl border border-dashed border-black/20 p-10 text-center bg-white/70" data-testid="farmers-farms-signin">
            <House size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
            <div className="font-serif-display text-2xl">Sign in to add a farm.</div>
            <button
              onClick={() => requireSignIn("manage farms")}
              data-testid="farmers-farms-signin-cta"
              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
            >
              Sign in
            </button>
          </div>
        ) : busy ? (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-40 rounded-2xl bg-white/60 animate-pulse" />)}
          </div>
        ) : farms.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-black/20 p-10 text-center bg-white/70" data-testid="farmers-farms-empty">
            <House size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
            <div className="font-serif-display text-2xl">No farms yet.</div>
            <p className="mt-1 text-sm text-[#3A3830]">Add a farm to unlock the diary, calendar and personalized alerts.</p>
            <Link to={fpPath("/farms/new")} className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90">
              <Plus size={11} weight="bold" /> Add a farm
            </Link>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="farmers-farms-list">
            {farms.map((f) => (
              <Link
                key={f.id}
                to={fpPath(`/farms/${f.id}`)}
                data-testid={`farmers-farm-${f.id}`}
                className="rounded-2xl border border-black/10 bg-white p-5 hover:border-[color:var(--np-brand,#B85F2A)] transition"
              >
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
                  <House size={11} weight="fill" /> {f.privacy || "private"}
                  {f.size && <><span className="text-[#3A3830]">·</span><span>{f.size} {f.size_unit || ""}</span></>}
                </div>
                <div className="mt-1 font-serif-display text-xl leading-tight">{f.name}</div>
                {(f.village || f.district || f.state || f.country) && (
                  <div className="mt-1 text-xs text-[#3A3830] flex items-center gap-1">
                    <MapPin size={11} weight="fill" /> {[f.village, f.district, f.state, f.country].filter(Boolean).join(", ")}
                  </div>
                )}
                {(f.crops || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {f.crops.slice(0, 6).map((c) => (
                      <span key={c} className="rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">{c}</span>
                    ))}
                  </div>
                )}
                <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-mono-tech tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                  Open <ArrowRight size={10} weight="bold" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}


/**
 * FarmersFarmNew — /farmers/farms/new
 * 4-step onboarding wizard (Location → Basics → Crops → Livestock & Equipment).
 * Fires POST /api/farmers/farms on final submit; hands the caller straight
 * into /farmers/farms/:id so the new farm becomes "their" farm immediately.
 */
export function FarmersFarmNew() {
  const { user, loading, requireSignIn } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", country: "", state: "", district: "", village: "",
    latitude: "", longitude: "", size: "", size_unit: "acre",
    irrigation_type: "", rain_fed: false,
    crops: "", trees: "", soil_types: "", water_sources: "",
    livestock: "", poultry: "", aquaculture: "",
    practices: "", organic_status: "", notes: "",
    privacy: "private",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) requireSignIn("add a farm");
  }, [user, loading, requireSignIn]);

  // Prefill from shared header location if the user set one.
  useEffect(() => {
    try {
      const raw = localStorage.getItem("np:user_location");
      if (!raw) return;
      const loc = JSON.parse(raw);
      setForm((f) => ({
        ...f,
        country:  f.country  || loc.country  || "",
        state:    f.state    || loc.state    || "",
        district: f.district || loc.city     || "",
        village:  f.village  || loc.village  || "",
        latitude:  f.latitude  || (loc.lat != null ? String(loc.lat.toFixed(6)) : ""),
        longitude: f.longitude || (loc.lon != null ? String(loc.lon.toFixed(6)) : ""),
      }));
    } catch { /* noop */ }
  }, []);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation is not supported here.");
    navigator.geolocation.getCurrentPosition((p) => {
      setForm((f) => ({ ...f, latitude: String(p.coords.latitude.toFixed(6)), longitude: String(p.coords.longitude.toFixed(6)) }));
      toast.success("Coordinates captured");
    }, () => toast.error("Could not read your location."));
  };

  if (loading) {
    return <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5"><div className="max-w-[700px] mx-auto animate-pulse h-64 bg-white/60 rounded-2xl" /></main>;
  }
  if (!user) {
    return (
      <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="farmers-farm-new-signin">
        <div className="max-w-[720px] mx-auto text-center rounded-2xl border border-dashed border-black/20 p-10 bg-white/70 mt-6">
          <House size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
          <div className="font-serif-display text-3xl">Sign in to add a farm.</div>
          <p className="mt-1 text-sm text-[#3A3830]">Farm records unlock your private diary, calendar and personalized alerts.</p>
          <button
            onClick={() => requireSignIn("add a farm")}
            data-testid="farmers-farm-new-signin-cta"
            className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            Sign in
          </button>
          <div className="mt-3">
            <Link to={fpPath("/")} className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline">
              Back to Farmers Pulse
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const parseList = (s, cap = 40) =>
    (s || "").split(",").map((x) => x.trim()).filter(Boolean).slice(0, cap);

  const submit = async () => {
    if (form.name.trim().length < 2) { setStep(2); return toast.error("Please give your farm a name."); }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        country: form.country.trim() || undefined,
        state: form.state.trim() || undefined,
        district: form.district.trim() || undefined,
        village: form.village.trim() || undefined,
        latitude: form.latitude ? parseFloat(form.latitude) : undefined,
        longitude: form.longitude ? parseFloat(form.longitude) : undefined,
        size: form.size ? parseFloat(form.size) : undefined,
        size_unit: form.size_unit || undefined,
        irrigation_type: form.irrigation_type.trim() || undefined,
        rain_fed: form.rain_fed,
        crops:         parseList(form.crops, 40),
        trees:         parseList(form.trees, 40),
        soil_types:    parseList(form.soil_types, 8),
        water_sources: parseList(form.water_sources, 8),
        livestock:     parseList(form.livestock, 20),
        poultry:       parseList(form.poultry, 20),
        aquaculture:   parseList(form.aquaculture, 20),
        practices:     parseList(form.practices, 20),
        organic_status: form.organic_status.trim() || undefined,
        notes: form.notes.trim() || undefined,
        privacy: form.privacy,
      };
      const { data } = await http.post("/farmers/farms", payload);
      toast.success("Farm added — welcome to your daily companion.");
      nav(fpPath(`/farms/${data.farm.id}`));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const gotoStep = (n) => setStep(Math.min(4, Math.max(1, n)));
  const next = () => (step === 4 ? submit() : gotoStep(step + 1));
  const back = () => gotoStep(step - 1);

  const STEPS = [
    { n: 1, label: "Location" },
    { n: 2, label: "Farm basics" },
    { n: 3, label: "Crops & trees" },
    { n: 4, label: "Livestock & equipment" },
  ];

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="farmers-farm-new">
      <div className="max-w-[860px] mx-auto">
        <Link to={fpPath("/farms")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]">
          <ArrowLeft size={11} weight="bold" /> Your farms
        </Link>
        <div className="mt-3 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#2D8659)]">
          Set Up My Farm · Free
        </div>
        <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
          Tell FarmersPulse about your farm.
        </h1>
        <p className="mt-2 text-sm text-[#3A3830] max-w-2xl">
          Four short steps. Everything stays private unless you change privacy at the end. FarmersPulse uses this to give you weather, planting, harvest, pest and market info tuned to <em>your</em> land.
        </p>

        {/* Stepper */}
        <div className="mt-6 grid grid-cols-4 gap-2" data-testid="farm-wizard-steps">
          {STEPS.map((s) => {
            const active = step === s.n;
            const done = step > s.n;
            return (
              <button
                key={s.n}
                onClick={() => gotoStep(s.n)}
                data-testid={`farm-wizard-step-${s.n}`}
                className={`text-left rounded-xl border px-3 py-2 transition-colors ${
                  active ? "border-[#2D8659] bg-white"
                    : done ? "border-[#2D8659]/40 bg-[#2D8659]/5"
                    : "border-black/10 bg-white/60"
                }`}
              >
                <div className={`font-mono-tech text-[9px] tracking-[0.28em] uppercase ${active || done ? "text-[#2D8659]" : "text-[#8E8B7F]"}`}>
                  Step {s.n} / 4
                </div>
                <div className={`font-serif-display text-base leading-tight ${active ? "text-[#151312]" : "text-[#3A3830]"}`}>
                  {s.label}
                </div>
              </button>
            );
          })}
        </div>

        {/* Step body */}
        <div className="mt-5 rounded-2xl border border-black/10 bg-white p-5 md:p-7 space-y-4" data-testid={`farm-wizard-body-${step}`}>
          {step === 1 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Country"><input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="farm-input-country" placeholder="India" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
                <Field label="State / Province"><input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="farm-input-state" placeholder="Karnataka" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
                <Field label="District / County"><input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="farm-input-district" placeholder="Bengaluru Rural" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
                <Field label="Village / Town"><input value={form.village} onChange={(e) => setForm({ ...form, village: e.target.value })} data-testid="farm-input-village" placeholder="Whitefield" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Latitude"><input value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} data-testid="farm-input-lat" placeholder="12.98" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
                <Field label="Longitude"><input value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} data-testid="farm-input-lon" placeholder="77.59" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
                <div className="grid gap-2">
                  <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Use my location</label>
                  <button type="button" onClick={useMyLocation} data-testid="farm-use-location" className="rounded-lg border border-black/15 bg-white px-3 py-2 text-sm hover:bg-black/5 inline-flex items-center gap-1.5">
                    <MapPin size={13} weight="fill" className="text-[color:var(--np-brand,#2D8659)]" /> Capture GPS
                  </button>
                </div>
              </div>
              <p className="text-xs text-[#8E8B7F]">
                Your coordinates are only used to fetch weather, calendar and nearby reports — never sold, shared or shown to other users unless you set the privacy to Public.
              </p>
            </>
          )}

          {step === 2 && (
            <>
              <Field label="Farm name *">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="farm-input-name" placeholder="e.g. Meenachil family orchard" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Size"><input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} data-testid="farm-input-size" placeholder="2.5" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
                <Field label="Unit">
                  <select value={form.size_unit} onChange={(e) => setForm({ ...form, size_unit: e.target.value })} data-testid="farm-input-unit" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm">
                    <option value="acre">acre</option>
                    <option value="hectare">hectare</option>
                    <option value="sqm">sqm</option>
                    <option value="cent">cent (India)</option>
                    <option value="rai">rai (Thailand)</option>
                  </select>
                </Field>
                <Field label="Organic status"><input value={form.organic_status} onChange={(e) => setForm({ ...form, organic_status: e.target.value })} data-testid="farm-input-organic" placeholder="Organic · Transitioning · Conventional" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Soil types (comma-separated)"><input value={form.soil_types} onChange={(e) => setForm({ ...form, soil_types: e.target.value })} data-testid="farm-input-soil" placeholder="Loam, red laterite" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
                <Field label="Water sources"><input value={form.water_sources} onChange={(e) => setForm({ ...form, water_sources: e.target.value })} data-testid="farm-input-water" placeholder="Well, canal, rain" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
                <Field label="Irrigation"><input value={form.irrigation_type} onChange={(e) => setForm({ ...form, irrigation_type: e.target.value })} data-testid="farm-input-irrigation" placeholder="Drip · flood · sprinkler" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
                <label className="inline-flex items-center gap-2 mt-6 text-sm">
                  <input type="checkbox" checked={form.rain_fed} onChange={(e) => setForm({ ...form, rain_fed: e.target.checked })} data-testid="farm-input-rainfed" /> Rain-fed
                </label>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <Field label="Crops (comma-separated)"><input value={form.crops} onChange={(e) => setForm({ ...form, crops: e.target.value })} data-testid="farm-input-crops" placeholder="Rice, Coconut, Black pepper, Tomato" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Trees (comma-separated)"><input value={form.trees} onChange={(e) => setForm({ ...form, trees: e.target.value })} data-testid="farm-input-trees" placeholder="Mango, Jackfruit, Neem" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
              <Field label="Practices (comma-separated)"><input value={form.practices} onChange={(e) => setForm({ ...form, practices: e.target.value })} data-testid="farm-input-practices" placeholder="Rotation, cover cropping, mulching" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
              <p className="text-xs text-[#8E8B7F]">
                Adding crops here lets FarmersPulse show planting windows, harvest countdowns and nearby pest reports specific to what you grow.
              </p>
            </>
          )}

          {step === 4 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Livestock (comma-separated)"><input value={form.livestock} onChange={(e) => setForm({ ...form, livestock: e.target.value })} data-testid="farm-input-livestock" placeholder="Cows, goats, sheep" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
                <Field label="Poultry"><input value={form.poultry} onChange={(e) => setForm({ ...form, poultry: e.target.value })} data-testid="farm-input-poultry" placeholder="Chicken, duck" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
                <Field label="Aquaculture"><input value={form.aquaculture} onChange={(e) => setForm({ ...form, aquaculture: e.target.value })} data-testid="farm-input-aquaculture" placeholder="Tilapia, shrimp" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
              </div>
              <Field label="Notes / anything worth knowing">
                <LangInput as="textarea" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} testid="farm-input-notes" placeholder="History, unique challenges, community context, equipment you have or need." />
              </Field>
              <Field label="Privacy">
                <select value={form.privacy} onChange={(e) => setForm({ ...form, privacy: e.target.value })} data-testid="farm-input-privacy" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm">
                  <option value="private">Private — only me</option>
                  <option value="community">Community — nearby farmers</option>
                  <option value="public">Public — anyone can view</option>
                  <option value="research">Research — anonymized research access</option>
                </select>
              </Field>
            </>
          )}

          <div className="pt-2 flex justify-between gap-2 flex-wrap">
            <button
              onClick={back}
              disabled={step === 1}
              data-testid="farm-wizard-back"
              className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5 disabled:opacity-40"
            >
              <ArrowLeft size={11} weight="bold" className="inline mr-1" /> Back
            </button>
            <div className="flex gap-2">
              <button onClick={() => nav(fpPath("/farms"))} className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">Cancel</button>
              <button
                onClick={next}
                disabled={saving}
                data-testid={step === 4 ? "farm-submit" : `farm-wizard-next-${step}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#2D8659] text-white px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#0F3D2A] disabled:opacity-50"
              >
                {step === 4 ? (
                  <><PaperPlaneTilt size={11} weight="fill" /> {saving ? "Saving…" : "Set up my farm"}</>
                ) : (
                  <>Continue <ArrowRight size={11} weight="bold" /></>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}


const DIARY_TYPES = [
  "planting", "germination", "transplanting", "flowering", "fruiting",
  "harvest", "irrigation", "rain", "fertilizer", "compost", "soil",
  "pest", "disease", "pollinators", "wildlife", "damage",
  "flood", "drought", "frost", "heat", "wind", "fire",
  "note", "other",
];

/**
 * FarmersFarmDetail — /farmers/farms/:id
 * Shows farm details + diary timeline + inline "add diary entry" form.
 */
export function FarmersFarmDetail() {
  return <FarmDetailInner />;
}

function FarmDetailInner() {
  const { user, requireSignIn } = useAuth();
  const nav = useNavigate();
  const { id } = useReactRouterParams();
  const [state, setState] = useState({ loading: true, farm: null, is_owner: false });
  const [diary, setDiary] = useState([]);
  const [diaryLoading, setDiaryLoading] = useState(true);
  const [newEntry, setNewEntry] = useState({ entry_type: "note", title: "", body: "", crop: "", tags: "", privacy: "private" });
  const [saving, setSaving] = useState(false);

  const reload = () => {
    setState({ loading: true, farm: null, is_owner: false });
    http.get(`/farmers/farms/${id}`)
      .then(({ data }) => setState({ loading: false, farm: data.farm, is_owner: data.is_owner }))
      .catch(() => setState({ loading: false, farm: null, is_owner: false }));
  };
  const loadDiary = () => {
    setDiaryLoading(true);
    http.get(`/farmers/farms/${id}/diary?limit=60`)
      .then(({ data }) => setDiary(data.entries || []))
      .catch(() => setDiary([]))
      .finally(() => setDiaryLoading(false));
  };
  useEffect(() => { reload(); loadDiary(); }, [id]);

  const addEntry = async () => {
    if (!user) return requireSignIn("add a diary entry");
    setSaving(true);
    try {
      await http.post(`/farmers/farms/${id}/diary`, {
        entry_type: newEntry.entry_type,
        title: newEntry.title.trim() || undefined,
        body: newEntry.body.trim() || undefined,
        crop: newEntry.crop.trim() || undefined,
        tags: newEntry.tags.split(",").map((s) => s.trim()).filter(Boolean),
        privacy: newEntry.privacy,
      });
      toast.success("Diary entry added");
      setNewEntry({ entry_type: "note", title: "", body: "", crop: "", tags: "", privacy: "private" });
      loadDiary();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save entry"); }
    finally { setSaving(false); }
  };

  const removeEntry = async (eid) => {
    if (!window.confirm("Delete this diary entry?")) return;
    try {
      await http.delete(`/farmers/diary/${eid}`);
      toast.success("Entry deleted");
      loadDiary();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const deleteFarm = async () => {
    if (!window.confirm("Delete this farm and all its diary entries? This cannot be undone.")) return;
    try {
      await http.delete(`/farmers/farms/${id}`);
      toast.success("Farm deleted");
      nav(fpPath("/farms"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  if (state.loading) {
    return <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10"><div className="max-w-[1100px] mx-auto animate-pulse h-64 bg-white/60 rounded-2xl" /></main>;
  }
  if (!state.farm) {
    return (
      <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="farmers-farm-notfound">
        <div className="max-w-[720px] mx-auto text-center">
          <h1 className="font-serif-display text-3xl">Farm not found</h1>
          <p className="mt-2 text-sm text-[#3A3830]">This farm may be private or has been removed.</p>
          <Link to={fpPath("/farms")} className="mt-4 inline-flex text-[color:var(--np-brand,#B85F2A)] underline">Your farms</Link>
        </div>
      </main>
    );
  }
  const f = state.farm;
  const locLine = [f.village, f.district, f.state, f.country].filter(Boolean).join(", ");
  const summary = `${f.name}. ${locLine ? "Located in " + locLine + "." : ""} ${f.notes || ""}`.trim();

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="farmers-farm-detail">
      <div className="max-w-[1100px] mx-auto">
        <Link to={fpPath("/farms")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]">
          <ArrowLeft size={11} weight="bold" /> Your farms
        </Link>
        <header className="mt-4 rounded-2xl border border-black/10 bg-white p-6 md:p-8">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
            <House size={11} weight="fill" /> Farm · {f.privacy || "private"}
            {(f.privacy === "private" || f.privacy === "shared") && <EyeSlash size={11} weight="fill" className="text-[#3A3830]" />}
          </div>
          <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight" data-testid="farm-name">{f.name}</h1>
          {locLine && (
            <div className="mt-2 text-sm text-[#3A3830] inline-flex items-center gap-1">
              <MapPin size={12} weight="fill" /> {locLine}
              {f.latitude && f.longitude && <span className="ml-2 text-[#3A3830]">· {Number(f.latitude).toFixed(3)}, {Number(f.longitude).toFixed(3)}</span>}
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <ListenTranslateBar text={summary} testidPrefix="farm-tools" />
            <ShareButton title={f.name} text={locLine} variant="chip" testid="farm-share" />
            {state.is_owner && (
              <button onClick={deleteFarm} data-testid="farm-delete" className="ml-auto inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700">
                <Trash size={11} weight="fill" /> Delete
              </button>
            )}
          </div>
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            {f.size !== undefined && f.size !== null && <Stat label="Size" value={`${f.size} ${f.size_unit || ""}`} />}
            {f.irrigation_type && <Stat label="Irrigation" value={f.irrigation_type} />}
            {f.rain_fed !== undefined && <Stat label="Water" value={f.rain_fed ? "Rain-fed" : "Irrigated"} />}
            {f.organic_status && <Stat label="Status" value={f.organic_status} />}
          </div>
          {(f.crops || []).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {(f.crops || []).map((c) => (
                <Link key={c} to={fpPath(`/crops/${encodeURIComponent(c.toLowerCase())}`)} className="rounded-full bg-[#FBF2E1] border border-black/10 px-2.5 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase hover:border-black/40">{c}</Link>
              ))}
            </div>
          )}
          {f.notes && <p className="mt-4 text-sm text-[#3A3830] whitespace-pre-wrap leading-relaxed">{f.notes}</p>}
        </header>

        {/* Diary */}
        <section className="mt-8" data-testid="farm-diary-section">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight flex items-center gap-2">
              <Notebook size={22} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /> Farm diary
            </h2>
            <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
              {diary.length} entries
            </span>
          </div>

          {state.is_owner && (
            <div className="mt-4 rounded-2xl border border-black/10 bg-white p-5" data-testid="farm-diary-form">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Type">
                  <select value={newEntry.entry_type} onChange={(e) => setNewEntry({ ...newEntry, entry_type: e.target.value })} data-testid="diary-input-type" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm capitalize">
                    {DIARY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Crop"><input value={newEntry.crop} onChange={(e) => setNewEntry({ ...newEntry, crop: e.target.value })} data-testid="diary-input-crop" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
                <Field label="Privacy">
                  <select value={newEntry.privacy} onChange={(e) => setNewEntry({ ...newEntry, privacy: e.target.value })} data-testid="diary-input-privacy" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm">
                    <option value="private">Private</option>
                    <option value="community">Community</option>
                    <option value="public">Public</option>
                  </select>
                </Field>
              </div>
              <div className="mt-3">
                <Field label="Title (optional)">
                  <input value={newEntry.title} onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })} data-testid="diary-input-title" placeholder="e.g. First mango flowers on the West field" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
                </Field>
              </div>
              <div className="mt-3">
                <Field label="What happened?">
                  <LangInput as="textarea" rows={3} value={newEntry.body} onChange={(e) => setNewEntry({ ...newEntry, body: e.target.value })} testid="diary-input-body" placeholder="A few sentences — what did you plant, harvest, notice, protect?" />
                </Field>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button onClick={addEntry} disabled={saving} data-testid="diary-submit" className="inline-flex items-center gap-1.5 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
                  <Plus size={11} weight="bold" /> {saving ? "Saving…" : "Add entry"}
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 space-y-3" data-testid="farm-diary-list">
            {diaryLoading && Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-20 rounded-lg bg-white/60 animate-pulse" />)}
            {!diaryLoading && diary.length === 0 && (
              <div className="rounded-xl border border-dashed border-black/20 p-8 text-center bg-white/60" data-testid="farm-diary-empty">
                <Notebook size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
                <div className="font-serif-display text-xl">No diary entries yet.</div>
                {state.is_owner && <p className="text-sm text-[#3A3830] mt-1">Add your first entry above.</p>}
              </div>
            )}
            {diary.map((d) => (
              <div key={d.id} data-testid={`diary-entry-${d.id}`} className="rounded-lg border border-black/10 bg-white p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                      {d.entry_type}{d.crop ? ` · ${d.crop}` : ""}{d.privacy && d.privacy !== "private" ? ` · ${d.privacy}` : ""}
                    </div>
                    <div className="mt-1 font-serif-display text-lg leading-tight">{d.title || `${d.entry_type} note`}</div>
                    <div className="text-[11px] text-[#3A3830]">{d.entry_date}</div>
                    {d.body && <p className="mt-2 text-sm text-[#3A3830] whitespace-pre-wrap">{d.body}</p>}
                  </div>
                  {state.is_owner && (
                    <button onClick={() => removeEntry(d.id)} data-testid={`diary-delete-${d.id}`} className="rounded-full border border-black/15 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700 p-1">
                      <Trash size={11} weight="fill" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Soil & Water Pulse */}
        <SoilWaterPanel farmId={id} isOwner={state.is_owner} />
      </div>
    </main>
  );
}


const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-black/10 bg-[#FBF2E1]/40 p-3">
    <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</div>
    <div className="mt-1 font-serif-display text-base">{value || "—"}</div>
  </div>
);
